import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Popconfirm, message } from "antd";
import dayjs from "dayjs";
import AuthContext from "../../context/AuthContext";
import { useTicket, useUpdateTicket, usePromoteTicket, useDeleteTicket } from "../../queries/tickets";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell } from "../../components/ui/PageShell";
import { StatusTag } from "../../components/ui/StatusTag";
import { CommentThread } from "./CommentThread";

export const TicketDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userData, isAdmin } = useContext(AuthContext);
  const { data: ticket, isLoading, isError, error } = useTicket(id!);
  const updateTicket = useUpdateTicket();
  const promoteTicket = usePromoteTicket(id!);
  const deleteTicket = useDeleteTicket();

  const canPromote = userData?.role !== "final_user" && !ticket?.promotedTaskId;

  // Every role that can reach this page can PUT (admin/user on any ticket
  // within scope, final_user on their own client's ticket) — updateTicket
  // silently ignores a subject change for final_user, so this button only
  // ever needs to send { solved }.
  const handleToggleSolved = async () => {
    try {
      await updateTicket.mutateAsync({ id: id!, data: { solved: !ticket!.solved } });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not update the ticket");
    }
  };

  const handlePromote = async () => {
    try {
      await promoteTicket.mutateAsync();
      message.success("Ticket promoted to a task");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not promote the ticket");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTicket.mutateAsync(id!);
      navigate("/support");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not delete the ticket");
    }
  };

  return (
    <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
      {ticket && (
        <PageShell
          kind="detail"
          crumbs={[{ label: "Support", to: "/support" }, { label: "Ticket" }]}
          title={ticket.subject}
          meta={[
            {
              label: "Status",
              value: ticket.solved
                ? <StatusTag label="Solved" tone="ok" dot />
                : <StatusTag label="Open" tone="warn" dot />,
            },
            { label: "Raised", value: <span className="q-num">{dayjs(ticket.createdAt).format("D MMM YYYY")}</span> },
            { label: "Replies", value: <span className="q-num">{ticket.comments.length}</span> },
            ...(ticket.promotedTaskId
              ? [{ label: "Linked task", value: (
                  <Button type="link" style={{ padding: 0, height: "auto", fontWeight: 600 }}
                    onClick={() => navigate(`/tasks/${ticket.promotedTaskId}`)}>
                    View task →
                  </Button>
                ) }]
              : []),
          ]}
          actions={
            <>
              <Button onClick={handleToggleSolved} loading={updateTicket.isPending}>
                {ticket.solved ? "Reopen" : "Mark solved"}
              </Button>
              {canPromote && (
                <Button onClick={handlePromote} loading={promoteTicket.isPending}>Promote to task</Button>
              )}
              {isAdmin && (
                <Popconfirm title="Delete this ticket?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={handleDelete}>
                  <Button danger>Delete</Button>
                </Popconfirm>
              )}
            </>
          }
        >
          <CommentThread ticketId={ticket._id} comments={ticket.comments} />
        </PageShell>
      )}
    </DisplayComponent>
  );
};
